import { useMemo, useState } from 'react'
import {
  buildTopSpendRows,
  formatPct,
  formatUsd,
  getSectionData,
} from '@/components/gcp/gcpHelpers'
import type { GcpAnalysisResult } from '@/services/gcpCosts'

interface GcpTopSpendTableProps {
  analysis: GcpAnalysisResult
}

function scrollToSection(anchorId: string) {
  const el = document.getElementById(`gcp-section-${anchorId}`)
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export default function GcpTopSpendTable({ analysis }: GcpTopSpendTableProps) {
  const [showAll, setShowAll] = useState(false)
  const billingSection = getSectionData(analysis, 'billing')

  const rows = useMemo(
    () => (billingSection ? buildTopSpendRows(billingSection, analysis._meta.analysis_months) : []),
    [billingSection, analysis._meta.analysis_months]
  )

  const visibleRows = showAll ? rows : rows.slice(0, 15)

  return (
    <div className="space-y-3">
      <div>
        <h3 className="container-title">Where the money goes</h3>
        <p className="mt-1 text-sm text-gray-500">
          Top line items across all services — ranked by estimated monthly cost.
        </p>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">No billing line items available.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Rank</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Service</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">What it is</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Est. monthly</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">% of total</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Trend</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleRows.map((row, index) => (
                  <tr key={row.id} className="align-top">
                    <td className="px-3 py-2 text-gray-700">{index + 1}</td>
                    <td className="px-3 py-2 text-gray-900">
                      {row.anchorId ? (
                        <button
                          type="button"
                          className="font-medium text-brand-700 hover:underline"
                          onClick={() => scrollToSection(row.anchorId!)}
                        >
                          {row.service}
                        </button>
                      ) : (
                        row.service
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-800">{row.description}</td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900">
                      {formatUsd(row.estimatedMonthlyCost)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {formatPct(row.pctOfTotal)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {row.trend != null ? (
                        <span className={row.trend >= 0 ? 'text-red-700' : 'text-green-700'}>
                          {row.trend >= 0 ? '+' : ''}
                          {formatPct(row.trend)}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rows.length > 15 ? (
            <button
              type="button"
              className="mt-3 text-sm font-medium text-brand-700 hover:underline"
              onClick={() => setShowAll((value) => !value)}
            >
              {showAll ? 'Show top 15' : `Show all (${rows.length})`}
            </button>
          ) : null}
        </>
      )}
    </div>
  )
}
